import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  CheckCircle, XCircle, Loader2, ArrowRight, Trophy, LogIn,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const LOGO_URL = '/logo.webp';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Stripe / PayPal post-checkout landing page.
 *
 * IMPORTANT: this page is a **public** route (no ProtectedRoute wrapper).
 * On mobile browsers with tracking protection, the auth cookie can be
 * stripped on the cross-site redirect from `checkout.stripe.com`, which
 * used to bounce paying customers to /login. The Stripe webhook is what
 * actually unlocks the purchase server-side — this page only confirms
 * payment status using a public Stripe status endpoint and shows a CTA
 * back into the app (which re-authenticates naturally if needed).
 */
export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { user, api } = useAuth();
  const [status, setStatus] = useState('processing'); // processing | success | error
  const [message, setMessage] = useState('Verifying your payment...');
  const [programId, setProgramId] = useState(null);

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyPayment = async () => {
    const sessionId = searchParams.get('session_id');
    const paypal = searchParams.get('paypal');
    const orderId = searchParams.get('token'); // PayPal returns order ID as 'token'
    const programIdParam = searchParams.get('program_id');

    setProgramId(programIdParam);

    if (!sessionId && !paypal) {
      setStatus('error');
      setMessage('Invalid payment session. Please try again.');
      return;
    }

    try {
      if (sessionId) {
        // Stripe — use the PUBLIC status endpoint so this works even if
        // the auth cookie got stripped during the cross-site redirect.
        const res = await fetch(
          `${API}/api/payments/stripe/status?session_id=${encodeURIComponent(sessionId)}`,
          { credentials: 'include' },
        );
        const data = await res.json();
        if (data.paid) {
          setStatus('success');
          setMessage('Payment confirmed! Your purchase is being unlocked.');
          // Fire-and-forget verify call when the user IS authed — quick UI
          // unlock instead of waiting for the webhook. Safe to fail silently
          // (the webhook will catch up regardless).
          if (user && programIdParam) {
            try {
              await api().post(`/payments/stripe/verify?session_id=${sessionId}&program_id=${programIdParam}`);
            } catch (e) { console.debug('verify silent:', e.message); }
          }
          toast.success('Payment verified!');
        } else {
          setStatus('error');
          setMessage(`Payment not completed (status: ${data.payment_status || 'unknown'}).`);
        }
      } else if (paypal && orderId && programIdParam && user) {
        // PayPal capture still requires auth (different flow than Stripe).
        const response = await api().post(
          `/payments/paypal/capture?order_id=${orderId}&program_id=${programIdParam}`,
        );
        if (response.data.success) {
          setStatus('success');
          setMessage('Payment successful! You now have access to the program.');
          toast.success('PayPal payment verified!');
        } else {
          setStatus('error');
          setMessage(response.data.message || 'Payment capture failed.');
        }
      } else {
        setStatus('error');
        setMessage('Could not verify payment. Please log in to check your purchase.');
      }
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'An error occurred while verifying your payment.');
    }
  };

  return (
    <div className="min-h-screen bg-black" data-testid="payment-success-page">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-center">
          <Link to={user ? '/dashboard' : '/'}>
            <img src={LOGO_URL} alt="Ascend & Surpass" className="h-10 w-10" />
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border p-12 text-center max-w-lg w-full"
        >
          {status === 'processing' && (
            <>
              <Loader2 className="h-20 w-20 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Processing Payment
              </h1>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="h-20 w-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-12 w-12 text-green-500" />
              </div>
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Payment <span className="text-primary">Successful!</span>
              </h1>
              <p className="text-muted-foreground mb-8">{message}</p>

              <div className="space-y-4">
                {user ? (
                  <>
                    {programId && (
                      <Link to={`/program/${programId}`}>
                        <Button
                          className="w-full h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90"
                          data-testid="payment-start-program-btn"
                        >
                          <Trophy className="mr-2 h-5 w-5" /> Start Your Program
                        </Button>
                      </Link>
                    )}
                    <Link to="/dashboard">
                      <Button
                        variant="outline"
                        className="w-full h-12 border-white/20 text-white hover:bg-white/10 font-heading tracking-wider uppercase"
                        data-testid="payment-go-dashboard-btn"
                      >
                        Back to Dashboard <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground -mt-4 mb-2">
                      Log in to access your new purchase.
                    </p>
                    <Link to="/login">
                      <Button
                        className="w-full h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90"
                        data-testid="payment-login-btn"
                      >
                        <LogIn className="mr-2 h-5 w-5" /> Log In to Continue
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-20 w-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              <h1 className="font-heading text-3xl text-white uppercase tracking-wider mb-4">
                Payment <span className="text-red-500">Failed</span>
              </h1>
              <p className="text-muted-foreground mb-8">{message}</p>

              <div className="space-y-4">
                <Link to={user ? '/programs' : '/login'}>
                  <Button className="w-full h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90">
                    {user ? 'Try Again' : 'Log In'} <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to={user ? '/dashboard' : '/'}>
                  <Button
                    variant="outline"
                    className="w-full h-12 border-white/20 text-white hover:bg-white/10 font-heading tracking-wider uppercase"
                  >
                    {user ? 'Back to Dashboard' : 'Home'}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};
